import * as React from 'react';

import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useOnClickOutside} from 'src/hooks/useOnClickOutside';

import sectionCss from './dashboard-builder-section.css';
import vizTileCss from './dashboard-builder-viz-tile.css';


export type SectionRowProps = {
  label: string;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onSaveLabel: (label: string) => void;
  onDelete: () => void;
};

export const SectionRow = React.memo(
  ({
    label,
    canEdit,
    isEditing,
    onStartEdit,
    onSaveLabel,
    onDelete,
  }: SectionRowProps) => {
    const {t} = useLabelTranslation();
    const inputContainerRef = React.useRef<HTMLDivElement>(null);
    const isHandlingBlurViaClickOutside = React.useRef(false);
    const [draft, setDraft] = React.useState(label);

    React.useEffect(() => {
      if (!isEditing) {
        setDraft(label);
      }
    }, [label, isEditing]);

    useOnClickOutside(inputContainerRef, () => {
      if (!isEditing) {
        return;
      }
      isHandlingBlurViaClickOutside.current = true;
      const trimmed = draft.trim();
      onSaveLabel(trimmed || t('New_Section', 'New Section'));
      isHandlingBlurViaClickOutside.current = false;
    });

    const handleBlur = () => {
      if (isHandlingBlurViaClickOutside.current) {
        return;
      }
      const trimmed = draft.trim();
      onSaveLabel(trimmed || t('New_Section', 'New Section'));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
      } else if (e.key === 'Escape') {
        setDraft(label);
        onSaveLabel(label);
      }
    };

    const handleLabelKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onStartEdit();
      }
    };

    // Select on focus so the user can type over the "New Section"
    // placeholder without manually clearing it first.
    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    if (isEditing) {
      return (
        <div className={sectionCss.sectionRow}>
          <div
            ref={inputContainerRef}
            className={sectionCss.sectionInputContainer}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              autoFocus
              size="small"
              value={draft}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft(e.target.value)
              }
              onFocus={handleInputFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={sectionCss.sectionRow}>
        <span
          className={
            canEdit ? sectionCss.sectionLabelEditable : sectionCss.sectionLabel
          }
          {...(canEdit
            ? {
                onClick: onStartEdit,
                onKeyDown: handleLabelKeyDown,
                role: 'button' as const,
                tabIndex: 0,
                'aria-label': t('Rename_section', 'Rename section'),
              }
            : {})}
        >
          {label || t('New_Section', 'New Section')}
        </span>
        {canEdit && (
          <div className={sectionCss.sectionActions}>
            <button
              type="button"
              className={vizTileCss.tileIconBtn}
              aria-label={t('Rename_section', 'Rename section')}
              onClick={onStartEdit}
            >
              <Icon name="pencil" size="small" />
            </button>
            <button
              type="button"
              className={vizTileCss.tileIconBtn}
              aria-label={t('Delete_section', 'Delete section')}
              onClick={onDelete}
            >
              <Icon name="times" size="small" />
            </button>
          </div>
        )}
      </div>
    );
  },
);
